import { format, addDays, subDays } from 'date-fns';

export interface Document {
  _id: string;
  id?: string; // For backward compatibility
  title: string;
  description: string;
  category: string;
  fileUrl: string;
  fileName: string;
  fileSize: number;
  fileType: string;
  tags?: string[];
  downloads?: number;
  isPublished?: boolean;
  createdAt: string;
  updatedAt?: string;
  publishedAt?: string;
  uploadDate?: string; // For backward compatibility
  url?: string; // For backward compatibility
  size?: string; // For backward compatibility
}

export interface Video {
  _id: string;
  id?: string; // For backward compatibility
  title: string;
  description: string;
  url: string;
  thumbnail?: string;
  duration: string;
  category: 'training' | 'update' | 'announcement' | 'tutorial' | 'other';
  tags?: string[];
  views?: number;
  isPublished?: boolean;
  createdAt: string;
  updatedAt?: string;
  publishedAt?: string;
}

export interface Action {
  id: string;
  title: string;
  description: string;
  dueDate: string;
  points: number;
  completed: boolean;
  priority: 'high' | 'medium' | 'low';
}

export interface WeeklyUpdate {
  id: string;
  weekOf: string;
  wins: string[];
  upcomingFocus: string[];
  decisionsRequired: string[];
  uploadDate: string;
}

export interface WorkstreamStatus {
  id: string;
  name: string;
  status: 'red' | 'amber' | 'green';
  description: string;
}

export interface DashboardData {
  workstreams: WorkstreamStatus[];
  yourContribution: string;
  weeklyScore: number;
}

// Mock Documents (deprecated - now using API)
export const mockDocuments: Partial<Document>[] = [
  {
    id: 'doc-1',
    title: 'Manufacturing Management System - Application Design and Features',
    category: 'System Architecture',
    uploadDate: format(subDays(new Date(), 5), 'yyyy-MM-dd'),
    fileType: 'pdf',
    size: '322 KB',
    url: '/documents/Manufacturing Management System - Application Design and Features.pdf',
  },
  {
    id: 'doc-2',
    title: 'Manufacturing Management System Architecture',
    category: 'System Architecture',
    uploadDate: format(subDays(new Date(), 3), 'yyyy-MM-dd'),
    fileType: 'pdf',
    size: '312 KB',
    url: '/documents/Manufacturing Management System Architecture.pdf',
  },
  {
    id: 'doc-3',
    title: 'Master Thesis: Scaling from 10M to $25M profitable',
    category: 'Business Strategy',
    uploadDate: format(subDays(new Date(), 10), 'yyyy-MM-dd'),
    fileType: 'pdf',
    size: '1.4 MB',
    url: '/documents/Master Thesis_ Scaling from 10M to _25M profitable_compressed (1).pdf',
  },
  {
    id: 'doc-4',
    title: 'Meta ewaste and ROHS Compliance',
    category: 'Compliance & Standards',
    uploadDate: format(subDays(new Date(), 2), 'yyyy-MM-dd'),
    fileType: 'pdf',
    size: '297 KB',
    url: '/documents/Meta ewaste and ROHS.pdf',
  },
  {
    id: 'doc-5',
    title: 'Stakeholder Communication Plan',
    category: 'Governance',
    uploadDate: format(subDays(new Date(), 7), 'yyyy-MM-dd'),
    fileType: 'doc',
    size: '856 KB',
  },
  {
    id: 'doc-6',
    title: 'Risk Register - Updated',
    category: 'Governance',
    uploadDate: format(subDays(new Date(), 1), 'yyyy-MM-dd'),
    fileType: 'xls',
    size: '642 KB',
  },
  {
    id: 'doc-7',
    title: 'Training Materials - Module 1',
    category: 'Training & Enablement',
    uploadDate: format(subDays(new Date(), 14), 'yyyy-MM-dd'),
    fileType: 'ppt',
    size: '5.6 MB',
  },
  {
    id: 'doc-8',
    title: 'Field Team Feedback Summary',
    category: 'Training & Enablement',
    uploadDate: format(subDays(new Date(), 4), 'yyyy-MM-dd'),
    fileType: 'pdf',
    size: '1.3 MB',
  },
];

// Mock Actions
export const mockActions: Action[] = [
  {
    id: 'action-1',
    title: 'Review and approve Q4 budget allocation',
    description: 'Review the proposed budget for Q4 transformation initiatives and provide approval or feedback.',
    dueDate: format(addDays(new Date(), 2), 'yyyy-MM-dd'),
    points: 15,
    completed: false,
    priority: 'high',
  },
  {
    id: 'action-2',
    title: 'Schedule stakeholder alignment meeting',
    description: 'Coordinate with key stakeholders for the monthly alignment session.',
    dueDate: format(addDays(new Date(), 1), 'yyyy-MM-dd'),
    points: 10,
    completed: false,
    priority: 'high',
  },
  {
    id: 'action-3',
    title: 'Provide feedback on training module draft',
    description: 'Review the draft training materials and provide input on content and delivery approach.',
    dueDate: format(addDays(new Date(), 4), 'yyyy-MM-dd'),
    points: 10,
    completed: false,
    priority: 'medium',
  },
  {
    id: 'action-4',
    title: 'Approve field team resource requests',
    description: 'Review and approve the additional resource requests from the field implementation team.',
    dueDate: format(addDays(new Date(), 3), 'yyyy-MM-dd'),
    points: 12,
    completed: false,
    priority: 'high',
  },
  {
    id: 'action-5',
    title: 'Review weekly progress dashboard',
    description: 'Review the latest progress metrics and identify any areas requiring attention.',
    dueDate: format(new Date(), 'yyyy-MM-dd'),
    points: 8,
    completed: true,
    priority: 'medium',
  },
  {
    id: 'action-6',
    title: 'Update executive steering committee',
    description: 'Prepare and deliver update to the executive steering committee on program status.',
    dueDate: format(addDays(new Date(), 5), 'yyyy-MM-dd'),
    points: 15,
    completed: false,
    priority: 'high',
  },
];

// Mock Weekly Update
export const mockWeeklyUpdate: WeeklyUpdate = {
  id: 'update-1',
  weekOf: format(new Date(), 'MMMM dd, yyyy'),
  wins: [
    'Successfully completed pilot rollout in 3 field locations with 94% adoption rate',
    'Secured executive approval for expanded budget allocation for Q4',
    'Onboarded 12 new field champions who will drive local implementation',
    'Resolved critical system integration issue ahead of schedule',
  ],
  upcomingFocus: [
    'Scale pilot learnings to 15 additional field locations',
    'Finalize training curriculum for wave 2 implementation',
    'Address resource constraints identified in risk register',
    'Prepare monthly steering committee presentation',
  ],
  decisionsRequired: [
    'Approve additional budget for accelerated training delivery ($45K)',
    'Select vendor for mobile app development (3 proposals submitted)',
    'Decide on timeline adjustment for wave 3 rollout (2-week delay proposed)',
  ],
  uploadDate: format(new Date(), 'yyyy-MM-dd'),
};

// Mock Dashboard Data
export const mockDashboardData: DashboardData = {
  workstreams: [
    {
      id: 'ws-1',
      name: 'Field Implementation',
      status: 'green',
      description: 'On track - Pilot sites performing above target',
    },
    {
      id: 'ws-2',
      name: 'Training & Enablement',
      status: 'amber',
      description: 'Minor delays in curriculum finalization',
    },
    {
      id: 'ws-3',
      name: 'Technology Integration',
      status: 'green',
      description: 'System integration completed successfully',
    },
    {
      id: 'ws-4',
      name: 'Change Management',
      status: 'amber',
      description: 'Resistance in 2 locations - mitigation plan active',
    },
    {
      id: 'ws-5',
      name: 'Governance & Reporting',
      status: 'green',
      description: 'All reporting mechanisms functioning well',
    },
  ],
  yourContribution: 'Your decisive action on budget approval this week enabled the team to accelerate training delivery. Your engagement in the stakeholder sessions helped secure buy-in from previously resistant leaders.',
  weeklyScore: 73,
};

// Motivational Messages
export const motivationalMessages = [
  'Momentum builds with consistent action. Every decision moves us forward.',
  'Your leadership drives transformation. Stay focused on what matters most.',
  'Progress is the sum of small wins. Keep the team aligned and moving.',
  'Clear priorities today create breakthrough results tomorrow.',
  'Your engagement this week sets the tone for the entire program.',
  'Transformation happens one decision at a time. You\'re making it happen.',
  'Consistency beats intensity. Your steady focus is driving real change.',
];

// Get random motivational message
export const getMotivationalMessage = (): string => {
  return motivationalMessages[Math.floor(Math.random() * motivationalMessages.length)];
};

// Calculate overall progress percentage
export const calculateProgress = (actions: Action[]): number => {
  if (actions.length === 0) return 0;
  const completedPoints = actions.filter(a => a.completed).reduce((sum, a) => sum + a.points, 0);
  const totalPoints = actions.reduce((sum, a) => sum + a.points, 0);
  return Math.round((completedPoints / totalPoints) * 100);
};
